/**
 * Heat, venting, the overheat lockout, shield regeneration, and the ultimate trickle.
 * Rules: heat cap reached → weapons lock for `overheatLockSeconds`; holding vent triples
 * dissipation but locks the weapons while it lasts; shields regenerate after four seconds without
 * damage and never during sudden death.
 */
import { SIM_DT } from '../constants.js';
import { secondsToTicks } from './state.js';
import { defOf, emit, isLive, mech } from './tick.js';
export function stepHeat(t, slot, input) {
    const c = mech(t, slot);
    const def = defOf(t, slot);
    const m = t.mods[slot];
    const venting = input.vent && c.alive && !m.stunned;
    if (venting !== c.venting) {
        c.venting = venting;
        emit(t, { t: 'vent', slot, on: venting });
    }
    let dissipation = def.heatDissipation * m.dissipationScale;
    if (c.venting)
        dissipation *= t.ctx.balance.ventDissipationScale;
    if (m.heatLock)
        dissipation = 0;
    c.heat = Math.max(0, c.heat - dissipation * SIM_DT);
    if (c.overheatTicks > 0)
        c.overheatTicks--;
}
/** Adds heat through the mech's heat scale; reaching the cap starts the overheat lockout. */
export function addHeat(t, slot, amount) {
    const c = mech(t, slot);
    const def = defOf(t, slot);
    c.heat += amount * t.mods[slot].heatScale;
    if (c.heat >= def.heatCap) {
        c.heat = def.heatCap;
        if (c.overheatTicks === 0) {
            c.overheatTicks = secondsToTicks(t.ctx.balance.overheatLockSeconds);
            emit(t, { t: 'overheat', slot });
        }
    }
}
/** Removes heat (cooling effects, target-heat weapons use `addHeat` on the target). */
export function removeHeat(t, slot, amount) {
    const c = mech(t, slot);
    c.heat = Math.max(0, c.heat - amount);
}
/** Weapons cannot fire: dead, not live, overheated, venting, stunned, or a no-weapons effect. */
export function weaponsLocked(t, slot) {
    const c = mech(t, slot);
    return !c.alive || !isLive(t) || c.overheatTicks > 0 || c.venting || t.mods[slot].noWeapons;
}
export function stepShields(t, slot) {
    const c = mech(t, slot);
    if (!c.alive)
        return;
    if (t.s.flow.phase === 'suddenDeath') {
        c.shield = 0;
        return;
    }
    if (c.shieldDelayTicks > 0) {
        c.shieldDelayTicks--;
        return;
    }
    const def = defOf(t, slot);
    if (t.mods[slot].noShieldRegen || c.shield >= def.shield)
        return;
    c.shield = Math.min(def.shield, c.shield + def.shieldRegen * SIM_DT);
}
export function stepUltTrickle(t, slot) {
    const c = mech(t, slot);
    if (!c.alive || t.s.flow.phase !== 'live' || t.mods[slot].noUltCharge)
        return;
    const before = c.ultCharge;
    c.ultCharge = Math.min(100, c.ultCharge + t.ctx.balance.ultimate.perSecond * SIM_DT);
    if (before < 100 && c.ultCharge >= 100)
        emit(t, { t: 'ultCharged', slot });
}
//# sourceMappingURL=resources.js.map